import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import moment from 'moment';
import flatten from 'lodash.flatten';
import compose from 'lodash.flowright';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import withModal from '../../components/global/modal/withModal';
import { DISCOVER_PURCHASE_SELECTION_MODAL } from '../../constants/modals';
import Button from '../../components/global/Button';
import { formatPriceForDisplay } from '../../util/format-money';
import { PPGD_JOIN_TYPE } from '../../constants/discover-album-folder';
import { CURRENCY } from '../../constants/currency';
import { getAsset } from '../../util/assets';
import {
  albumFolderHasCharity,
  isPrepaidOnlyAlbumFolder,
} from '../../util/album-folder';
import ContextualString from '../../components/contextual/ContextualString';
import { getPpaPrice } from '../../util/purchase-album';
import { ALBUM_SUBTYPE } from '../../constants/albums';
import {
  onSelectIdentity as onSelectIdentityAction,
  onDeselectIdentity as onDeselectIdentityAction,
  onSelectAlbumCustomPrint as onSelectAlbumCustomPrintAction,
  onDeselectAlbumCustomPrint as onDeselectAlbumCustomPrintAction,
} from '../../actions/discover-album-folder';
import withVirtualPageview from '../../components/global/hoc/withVirtualPageview';
import Alert from '../../components/global/Alert';
import PriceDisplay from '../../components/global/PriceDisplay';
import { MeWithIdentities } from '../../apps/consumer/graphql/queries/account';

class DiscoverAlbumFolderJoinTypeSelection extends Component {
  static propTypes = {
    albumFolder: PropTypes.shape({
      price: PropTypes.shape({
        value: PropTypes.number,
        currency: PropTypes.string,
      }).isRequired,
      limited_time_offer_active: PropTypes.bool,
      limited_time_offer_price: PropTypes.shape({
        value: PropTypes.number,
        currency: PropTypes.oneOf(Object.values(CURRENCY)),
      }),
      limited_time_offer_date_end: PropTypes.string,
      has_charity: PropTypes.bool,
      charity_description: PropTypes.string,
      join_without_purchase: PropTypes.bool,
    }).isRequired,
    identity: PropTypes.shape({
      id: PropTypes.string.isRequired,
      selectedAlbums: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          custom_prints: PropTypes.shape({
            records: PropTypes.arrayOf(
              PropTypes.shape({
                id: PropTypes.string,
                price: PropTypes.shape({
                  value: PropTypes.number,
                  currency: PropTypes.oneOf(Object.values(CURRENCY)),
                }),
                discount_price: PropTypes.shape({
                  value: PropTypes.number,
                  currency: PropTypes.oneOf(Object.values(CURRENCY)),
                }),
              }),
            ),
          }),
        }),
      ),
    }),
    onRequestClose: PropTypes.func.isRequired,
    onAfterSelect: PropTypes.func.isRequired,
    onSelectIdentity: PropTypes.func.isRequired,
    onSelectAlbumCustomPrint: PropTypes.func,
    onDeselectAlbumCustomPrint: PropTypes.func,
  };

  onSelectJoinType = (joinType, includeCustomPrints) => {
    const {
      onRequestClose,
      onAfterSelect,
      onSelectIdentity,
      onSelectAlbumCustomPrint,
      onDeselectAlbumCustomPrint,
      identity,
    } = this.props;

    onSelectIdentity(identity, joinType);

    const selectedAlbumCustomPrints = this.getPurchaseableAlbumCustomPrints();

    if (selectedAlbumCustomPrints.length) {
      selectedAlbumCustomPrints.forEach(albumCustomPrint =>
        includeCustomPrints
          ? onSelectAlbumCustomPrint({
              ...albumCustomPrint,
              includeShipping: true,
            })
          : // Completely remove all custom prints on any option
            // other than the bundle
            onDeselectAlbumCustomPrint(albumCustomPrint),
      );
    }

    onRequestClose();
    onAfterSelect(joinType);
  };

  /**
   * Get the price per photo from the first album that's
   * selected by an identity, if any.
   */
  getPricePerPhoto = () => {
    const { identity } = this.props;
    const [album] = identity?.selectedAlbums ?? [];

    return album?.photo_pricing?.digital_downloads_price?.value;
  };

  /**
   * Get the final subscription price, either the limited time offer if it
   * exists and it's active or the normal album folder price.
   *
   * @returns {number}
   */
  getFinalSubscriptionPrice = () => {
    const { albumFolder } = this.props;

    if (this.hasLimitedTimeOffer()) {
      return albumFolder.limited_time_offer_price.value;
    }

    // Fallback to album level subscription price (PPA)
    return this.getFullSubscriptionPrice();
  };

  /**
   * Get the normal album folder price.
   *
   * @returns {number}
   */
  getFullSubscriptionPrice = () => {
    const { albumFolder, identity } = this.props;

    // Album folder subscription takes precedence (PPG)
    if (albumFolder?.price?.value) {
      return albumFolder.price.value;
    }

    // Check for album level subscription prices (PPA)
    const { selectedAlbums = [] } = identity;
    return selectedAlbums.reduce((sum, album) => {
      return sum + getPpaPrice(album);
    }, 0);
  };

  /**
   * Returns the total and discounted price of all custom print packages
   * from all albums.
   *
   * @returns {*|{price: *, discountPrice: *}}
   */
  getAlbumCustomPrintsTotalDiscountedPrice = () => {
    const selectedAlbumCustomPrints = this.getPurchaseableAlbumCustomPrints();

    return selectedAlbumCustomPrints.reduce(
      (sum, albumCustomPrint) => ({
        price: sum.price + (albumCustomPrint?.price?.value ?? 0),
        discountPrice:
          sum.discountPrice + (albumCustomPrint?.discount_price?.value ?? 0),
      }),
      {
        price: 0,
        discountPrice: 0,
      },
    );
  };

  /**
   * Returns all the custom prints from all selected albums for
   * the current identity (flattened) or the album folder custom prints.
   *
   * @returns {Array} - The custom prints.
   */
  getPurchaseableAlbumCustomPrints = () => {
    const { identity, albumFolder } = this.props;

    const selectedAlbumsAlbumCustomPrints = flatten(
      (identity?.selectedAlbums ?? []).map(
        album => album?.custom_prints?.records ?? [],
      ),
    );
    const discountedAlbumCustomPrints = selectedAlbumsAlbumCustomPrints.filter(
      albumCustomPrint => albumCustomPrint.discount_price?.value > 0,
    );

    // Prioritize album selection album custom prints, if any.
    if (discountedAlbumCustomPrints.length > 0) {
      return discountedAlbumCustomPrints ?? [];
    }

    // Fallback to the album folder's album custom prints, if any.
    return (
      albumFolder?.album_custom_prints?.records?.filter(
        albumCustomPrint => albumCustomPrint.discount_price?.value > 0,
      ) ?? []
    );
  };

  /**
   * Checks if there is a limited time offer.
   *
   * @returns {boolean}
   */
  hasLimitedTimeOffer = () => {
    const { albumFolder } = this.props;

    return (
      albumFolder.limited_time_offer_active &&
      !!albumFolder.limited_time_offer_date_end &&
      !!albumFolder.limited_time_offer_price &&
      !!albumFolder.limited_time_offer_price.value
    );
  };

  renderSubscriptionPrice = () => {
    const { albumFolder } = this.props;
    const price = this.getFullSubscriptionPrice();
    const finalPrice = this.getFinalSubscriptionPrice();

    if (price === finalPrice) {
      return (
        <PriceDisplay
          price={price}
          currency={albumFolder?.price?.currency ?? 'USD'}
        />
      );
    }

    return (
      <PriceDisplay
        oldPrice={price}
        price={finalPrice}
        currency={albumFolder?.price?.currency ?? 'USD'}
        showDiscount
      />
    );
  };

  renderCharityDetails = () => {
    const { albumFolder } = this.props;

    return albumFolderHasCharity(albumFolder) ? (
      <div className="text-center text-xs mt-1">
        {albumFolder.charity_description || (
          <ContextualString
            id="albumFolderPurchaseCharityDefaultMessage"
            type={albumFolder.album_type}
            subtype={albumFolder.album_subtype}
            defaultMessage="*50% goes to charity. Thank you!"
          />
        )}
      </div>
    ) : null;
  };

  renderAlbumCustomPrintSelection = () => {
    const { albumFolder } = this.props;
    const selectedAlbumCustomPrints = this.getPurchaseableAlbumCustomPrints();

    if (!selectedAlbumCustomPrints.length) {
      return null;
    }

    const [albumCustomPrint] = selectedAlbumCustomPrints;
    const albumCustomPrintLabel =
      albumCustomPrint.custom_print.name || 'Custom Print';
    const { price, discountPrice } =
      this.getAlbumCustomPrintsTotalDiscountedPrice();

    const totalPrice = this.getFullSubscriptionPrice() + price;
    const finalPrice =
      this.getFinalSubscriptionPrice() + (discountPrice || price);

    return (
      <Fragment>
        <Button
          btnBlock
          onClick={() => this.onSelectJoinType(PPGD_JOIN_TYPE.PURCHASE, true)}
          className="btn-custom-prints"
        >
          Get{' '}
          <ContextualString
            id="purchaseSubscriptionText"
            type={albumFolder.album_type}
            subtype={albumFolder.album_subtype}
            defaultMessage="all my photos"
          />{' '}
          + {albumCustomPrintLabel}{' '}
          <span className="text-nowrap">
            (
            <PriceDisplay
              oldPrice={totalPrice}
              price={finalPrice}
              currency={albumFolder?.price?.currency ?? 'USD'}
              showDiscount
            />
            )
          </span>
        </Button>

        <span className="d-block text-center">or</span>
      </Fragment>
    );
  };

  render() {
    const { albumFolder } = this.props;
    const pricePerPhoto = this.getPricePerPhoto();
    const hasLimitedTimeOffer = this.hasLimitedTimeOffer();
    const fullSubscriptionPrice = this.getFullSubscriptionPrice();
    const finalSubscriptionPrice = this.getFinalSubscriptionPrice();
    const subscriptionDiscountPrice =
      fullSubscriptionPrice - finalSubscriptionPrice;

    return (
      <div>
        <div className="d-flex flex-column align-items-center mb-1">
          <div>
            <img
              src={getAsset('/photopass.svg')}
              alt=""
              width="100px"
              className="mx-auto mb-2"
            />
          </div>
          <h3 className="ml-1 mb-0 text-center">
            <ContextualString
              id="joinTypeSelectionHeader"
              type={albumFolder.album_type}
              subtype={albumFolder.album_subtype}
              defaultMessage="Get all your pics from {name} delivered to your phone!"
              values={{
                name: albumFolder.name,
              }}
            />
          </h3>
        </div>

        <div className="join-type-select-purchase p-2">
          {albumFolder.discovery_selection_info && (
            <Alert type="info" className="w-100 text-center text-sm">
              {albumFolder.discovery_selection_info}
            </Alert>
          )}
          <div className="join-type-title">
            <div className="join-type-title-content">
              {subscriptionDiscountPrice > 0 && (
                <h4 className="text-center">
                  <ContextualString
                    id="joinTypeSelectionDiscountTitle"
                    type={albumFolder.type}
                    subtype={albumFolder.album_subtype}
                    defaultMessage="Buy all your pics now and save ${price}!"
                    values={{
                      price: formatPriceForDisplay(
                        subscriptionDiscountPrice,
                        true,
                      ),
                    }}
                  />
                </h4>
              )}
            </div>
          </div>

          <div className="text-dark mb-2">
            {albumFolder.album_subtype === ALBUM_SUBTYPE.YOUTH_SPORTS && (
              <p>All your pics in Hi-res digital. Mobile delivery.</p>
            )}

            {hasLimitedTimeOffer && (
              <p className="mb-0">
                Only{' '}
                <PriceDisplay
                  price={finalSubscriptionPrice}
                  className="text-extra-bold"
                />{' '}
                when you purchase by{' '}
                <span className="text-extra-bold">
                  {moment(albumFolder.limited_time_offer_date_end).format(
                    'MMMM Do',
                  )}
                </span>{' '}
                (regular price is{' '}
                <PriceDisplay
                  price={fullSubscriptionPrice}
                  currency={albumFolder?.price?.currency ?? 'USD'}
                />
                ).
              </p>
            )}
          </div>

          {albumFolder.join_without_purchase ? (
            <Fragment>
              {this.renderAlbumCustomPrintSelection()}
              <Button
                btnBlock
                onClick={() => this.onSelectJoinType(PPGD_JOIN_TYPE.PURCHASE)}
                className="btn-purchase"
              >
                Get{' '}
                <ContextualString
                  id="purchaseSubscriptionText"
                  type={albumFolder.album_type}
                  subtype={albumFolder.album_subtype}
                  defaultMessage="all my photos"
                />
                <span className="ml-1">({this.renderSubscriptionPrice()})</span>
              </Button>

              <div className="text-center text-bold mt-2">
                <Button
                  btnStyle="reset"
                  onClick={() => this.onSelectJoinType(PPGD_JOIN_TYPE.JOIN)}
                  className="text-center text-bold btn-join"
                >
                  Not sure yet ?{' '}
                  <span className="text-primary">
                    {' '}
                    {isPrepaidOnlyAlbumFolder(albumFolder)
                      ? 'See other purchasing options'
                      : 'Preview Photos for Free'}
                  </span>
                </Button>
              </div>
            </Fragment>
          ) : (
            <Fragment>
              {this.renderAlbumCustomPrintSelection()}
              <Button
                btnBlock
                onClick={() => this.onSelectJoinType(PPGD_JOIN_TYPE.PURCHASE)}
                className="btn-purchase"
              >
                Get{' '}
                <ContextualString
                  id="purchaseSubscriptionText"
                  type={albumFolder.album_type}
                  subtype={albumFolder.album_subtype}
                  defaultMessage="all my photos"
                />{' '}
                for
                <span className="ml-1">{this.renderSubscriptionPrice()}</span>
              </Button>
            </Fragment>
          )}

          {this.renderCharityDetails()}
        </div>

        {/*
        This modal will be fired when the user needs to make a selection,
        but we want a different copy/UI for when it’s forced (i.e. this flag is
        true) vs when it’s natural (i.e. onboarding a PPG/D gallery renders a MultiSelect for album selection when albums are more than ALBUM_LENGTH_DROPDOWN_CONVERSION).
        */}
        {!albumFolder.join_without_purchase && (
          <div className="join-type-select-join p-2">
            <p className="text-dark text-center mb-2">
              For those who are camera shy and get less snaps, you can buy just
              the photos you want one by one{' '}
              {pricePerPhoto > 0 && (
                <Fragment>
                  (<PriceDisplay price={pricePerPhoto} /> / photo)
                </Fragment>
              )}{' '}
              after previewing them <strong>for free</strong>.
            </p>

            <Button
              btnBlock
              btnOutline
              onClick={() => this.onSelectJoinType(PPGD_JOIN_TYPE.JOIN)}
              className="btn-join"
            >
              Preview for Free &amp; Buy Later{' '}
              {pricePerPhoto > 0 && (
                <span className="text-sm d-block text-center">
                  (<PriceDisplay price={pricePerPhoto} /> / photo)
                </span>
              )}
            </Button>

            {this.renderCharityDetails()}
          </div>
        )}
      </div>
    );
  }
}

export { DiscoverAlbumFolderJoinTypeSelection };

const mapDispatchToProps = {
  onDeselectIdentity: onDeselectIdentityAction,
  onSelectIdentity: onSelectIdentityAction,
  onSelectAlbumCustomPrint: onSelectAlbumCustomPrintAction,
  onDeselectAlbumCustomPrint: onDeselectAlbumCustomPrintAction,
};

const withUserData = graphql(MeWithIdentities, {
  props: ({ data: { me } }) => ({
    account: me,
  }),
});

const DiscoverAlbumFolderJoinTypeSelectionContainer = compose(
  withRouter,
  withUserData,
  connect(null, mapDispatchToProps),
  withVirtualPageview({
    path: 'membership-selection',
    title: 'Membership Type Selection',
  }),
)(DiscoverAlbumFolderJoinTypeSelection);

export default withModal(DiscoverAlbumFolderJoinTypeSelectionContainer, {
  name: DISCOVER_PURCHASE_SELECTION_MODAL,
  hideHeader: true,
  size: 'sm',
  type: 'full',
});
